import { MenuLinks } from '@/constants/menu'
import { runWithToast } from '@/helpers/asyncToast.helper'
import { AnimationSkeleton } from '@/pages/ui/Skeleton'
import { formatRelativeTime, formatUtcExpiry } from '@/pages/dashboard/utils/formatRelativeTime'
import { loadCustomerPortalDashboard, type CustomerPortalDashboardVm } from '@/services/customerPortalDashboardService'
import { LicenseStatusTypes } from '@/services/licenseTypes'
import { subscriptionService } from '@/services/SubscriptionService'
import { useAppRole } from '@/hooks/useAppRole'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const shellCard = 'rounded-[1.2rem] border border-border bg-card shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]'
const kpiCard = `${shellCard} px-5 py-5`

const CustomerPortalDashboard = () => {
	const navigate = useNavigate()
	const { showMySubscriptions } = useAppRole()
	const [loading, setLoading] = useState(true)
	const [data, setData] = useState<CustomerPortalDashboardVm | null>(null)
	const [downloading, setDownloading] = useState(false)

	useEffect(() => {
		loadCustomerPortalDashboard()
			.then(setData)
			.finally(() => setLoading(false))
	}, [])

	const downloadSqlDbd = async () => {
		await runWithToast(async () => {
			await subscriptionService.downloadSqlDbd()
		}, {
			setLoading: setDownloading,
		})
	}

	if (loading) {
		return (
			<div className="min-w-0 w-full py-4 text-foreground">
				<AnimationSkeleton />
			</div>
		)
	}

	if (!data) {
		return (
			<div className="min-w-0 w-full py-4 text-foreground">
				<p className="text-[#caa59b]">Unable to load dashboard. Please refresh or contact support.</p>
			</div>
		)
	}

	const { profile, subscription, activeUserCount, activeDeviceCount, activeInstances, daysRemaining, trialTotalDays, activities } = data
	const firstName = profile.firstName?.trim() || profile.userName?.split('@')[0] || 'there'
	const maxUsers = subscription.maxUsers ?? 1
	const maxInstances = subscription.maxInstances ?? 1
	const isTrial = subscription.isTrial
	const tierLabel = isTrial ? 'Trial' : subscription.name || 'Licensed'
	const badgeLabel = isTrial ? 'TRIAL' : 'ACTIVE'
	const canDownload =
		subscription.licenseStatus === LicenseStatusTypes.Active && !subscription.pendingLicenseIssue && subscription.canUseProduct !== false
	const licenseKey = subscription.licenseKey
	const canUpgrade = subscription.canUpgrade ?? (isTrial && subscription.isExpired !== true)
	const showUpgradeAction = canUpgrade || subscription.isExpired === true
	const upgradeButtonLabel = subscription.isExpired
		? 'Renew subscription'
		: isTrial
			? 'Choose a plan'
			: 'Upgrade plan'
	const trialProgress =
		isTrial && trialTotalDays > 0 ? Math.min(100, Math.max(0, ((trialTotalDays - daysRemaining) / trialTotalDays) * 100)) : 0

	return (
		<div className="min-w-0 w-full text-foreground">
<header className="mb-7">
  <h1 className="font-display text-[2.35rem] font-semibold tracking-tight text-foreground lg:text-[2.5rem]">
    Welcome back,{" "}
    <span className="text-primary">
      {firstName}
    </span>
  </h1>

  <p className="mt-1.5 text-[0.95rem] font-medium text-muted-foreground">
    Here's what's happening with your SQLDBD license.
  </p>
</header>

			{/* License status banner */}
			<section className={`${shellCard} mb-6 px-6 py-6`}>
					<div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
						<div className="min-w-0 flex-1">
							<div className="mb-3 flex flex-wrap items-center gap-2">
								<span className="rounded-md bg-[#2a4a6e] px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#9ec5e8]">
									{badgeLabel}
								</span>
								{licenseKey && (
									<span className="font-mono text-xs text-muted-foreground break-all">{licenseKey}</span>
								)}
							</div>
							<h2 className="font-display text-xl font-semibold text-foreground lg:text-2xl">
								{subscription.name}
								{isTrial && daysRemaining >= 0 ? ` — ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left` : ''}
							</h2>
							<p className="mt-2 text-sm text-muted-foreground">Expires {formatUtcExpiry(subscription.expiredOn)}</p>
							{isTrial && trialTotalDays > 0 && (
								<div className="mt-4 max-w-xl">
									<div className="h-1.5 overflow-hidden rounded-full bg-background">
										<div
											className="h-full rounded-full bg-ember"
											style={{ width: `${trialProgress}%` }}
										/>
									</div>
									<p className="mt-2 text-xs text-muted-foreground">
										{daysRemaining} of {trialTotalDays} days remaining
									</p>
								</div>
							)}
						</div>
						<div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
							{showUpgradeAction ? (
								<button
									type="button"
									className="btn bg-primary px-5 text-white"
									onClick={() => navigate(MenuLinks.BuySubscriptionPlan)}
								>
									{upgradeButtonLabel}
								</button>
							) : (
								<Link to={MenuLinks.MySubscriptions} className="btn bg-primary px-5 text-center text-white">
									Manage subscription
								</Link>
							)}
							<button
								type="button"
								className="btn border border-primary/50 bg-transparent px-5 text-foreground hover:bg-card"
								onClick={downloadSqlDbd}
								disabled={!canDownload || downloading}
							>
								<i className={`${downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} me-2`} aria-hidden />
								{downloading ? 'Downloading…' : 'Download SQLDBD'}
							</button>
						</div>
					</div>
				</section>

				{/* KPI row */}
				<section className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
					<article className={kpiCard}>
						<div className="mb-3 flex items-start justify-between gap-3">
							<div className="font-mono text-xs uppercase tracking-[0.18em] text-[#cda59b]">Active devices</div>
							<i className="ri-server-line text-lg text-[#ff5b2f]" aria-hidden />
						</div>
						<div className="font-display text-[2.35rem] font-semibold tracking-tight">
							{activeDeviceCount}
						</div>
						<div className="mt-1.5 text-sm text-muted-foreground">Activated devices</div>
					</article>
					<article className={kpiCard}>
						<div className="mb-3 flex items-start justify-between gap-3">
							<div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">Active instances</div>
							<i className="ri-database-2-line text-lg text-primary" aria-hidden />
						</div>
						<div className="font-display text-[2.35rem] font-semibold tracking-tight">
							{activeInstances}/{maxInstances}
						</div>
						<div className="mt-1.5 text-sm text-muted-foreground">{isTrial ? 'Trial limit' : 'Plan limit'}</div>
					</article>
					<article className={kpiCard}>
						<div className="mb-3 flex items-start justify-between gap-3">
							<div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">Users</div>
							<i className="ri-user-line text-lg text-primary" aria-hidden />
						</div>
						<div className="font-display text-[2.35rem] font-semibold tracking-tight">
							{activeUserCount}/{maxUsers}
						</div>
						<div className="mt-1.5 text-sm text-muted-foreground">{isTrial ? 'Trial limit' : 'Plan limit'}</div>
					</article>
					<article className={kpiCard}>
						<div className="mb-3 flex items-start justify-between gap-3">
							<div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">Days remaining</div>
							<i className="ri-calendar-line text-lg text-primary" aria-hidden />
						</div>
						<div className="font-display text-[2.35rem] font-semibold tracking-tight">{daysRemaining}</div>
						<div className="mt-1.5 text-sm text-[#caa59b]">{isTrial ? 'Trial period' : 'Until renewal'}</div>
					</article>
					<article className={kpiCard}>
						<div className="mb-3 flex items-start justify-between gap-3">
							<div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">License tier</div>
							<i className="ri-key-2-line text-lg text-primary" aria-hidden />
						</div>
						<div className="font-display text-[2.35rem] font-semibold tracking-tight">{tierLabel}</div>
						<div className="mt-1.5 text-sm text-[#caa59b]">{isTrial ? '14-day evaluation' : 'Paid subscription'}</div>
					</article>
				</section>

				<section className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(280px,1fr)]">
					<article className={`${shellCard} px-6 py-6`}>
						<div className="mb-5 flex items-center justify-between gap-3">
							<h2 className="font-display text-[1.35rem] font-semibold">Recent Activity</h2>
							<Link to={MenuLinks.MySubscriptions} className="text-sm font-medium text-primary hover:underline">
								View all
							</Link>
						</div>
						{activities.length === 0 ? (
							<p className="text-sm text-muted-foreground">No activity yet. Activate your license in the Windows app to get started.</p>
						) : (
							<ul className="space-y-4">
								{activities.map((item) => (
									<li key={item.id} className="flex gap-3">
										<span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#ff5b2f]" aria-hidden />
										<div>
											<p className="text-[0.95rem] font-medium text-foreground">{item.text}</p>
											<p className="mt-0.5 text-sm text-[#caa59b]">{formatRelativeTime(item.at)}</p>
										</div>
									</li>
								))}
							</ul>
						)}
					</article>

					<article className={`${shellCard} px-6 py-6`}>
						<h2 className="mb-5 font-display text-[1.35rem] font-semibold">Quick Actions</h2>
						<ul className="space-y-2">
							{[
								{ label: 'Validate & activate license', to: MenuLinks.MySubscriptions },
								{ label: 'Manage instances', to: MenuLinks.MySubscriptions },
								...(showUpgradeAction
									? [{ label: isTrial ? 'Choose a subscription plan' : 'Upgrade or extend license', to: MenuLinks.BuySubscriptionPlan }]
									: [{ label: 'View subscription details', to: MenuLinks.MySubscriptions }]),
								// ...(showMySubscriptions
								// 	? [{ label: 'Offline license grant', to: MenuLinks.OfflineGrant }]
								// 	: []),
							].map((action) => (
								<li key={action.label}>
									<Link
										to={action.to}
										className="flex w-full items-center justify-between rounded-[0.9rem] border border-border bg-background/80 px-4 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-card"
									>
										{action.label}
										<i className="ri-arrow-right-up-line text-primary" aria-hidden />
									</Link>
								</li>
							))}
						</ul>
					</article>
				</section>
		</div>
	)
}

export default CustomerPortalDashboard
